import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Trophy, ArrowRight, Zap, Sparkles,
  Calendar, Mail, CreditCard, FileSpreadsheet, FileSignature, PenLine,
} from "lucide-react";
import { type NewPlan } from "@workspace/plans";
import { useOrg } from "@/lib/orgContext";
import { GlassNav, FadeUp, StaggerChildren, StaggerItem, CountUp, HoverLift } from "@/components/motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  LifecycleStrip,
  PersonaTabs,
  ProductPeeks,
  DisplayAccent,
  HeroVisual,
  FounderRow,
} from "@/components/landing/sections";
import { SALES_EMAIL, salesInquiryHref, STATUS_PAGE_URL } from "@/lib/support";

// Toggle to true to restore pricing + compare sections on the marketing page.
// Kept intact (not deleted) per direction to hide from live view but preserve code.
const SHOW_PRICING_SECTIONS = false;

const INTEGRATIONS = [
  { icon: FileSignature, label: "DocuSign" },
  { icon: PenLine, label: "Adobe Sign" },
  { icon: Calendar, label: "Google Calendar" },
  { icon: Mail, label: "Outlook" },
  { icon: CreditCard, label: "Stripe" },
  { icon: FileSpreadsheet, label: "CSV import / export" },
];

const FAQS = [
  {
    q: "How long does it take to get our existing coach contracts in?",
    a: "Most departments are live within a week. Bring your existing contracts as PDFs and our AI import pulls out the salary, term, buyout, and bonuses for you — no retyping. We'll help you load your roster and stand up a clause library out of your existing language, so you start with a real workspace, not a blank page.",
  },
  {
    q: "Can we run football, basketball, and Olympic sports out of one workspace?",
    a: "Yes. Your athletic department gets one private workspace, and inside it you can route approvals by sport, by role, or by dollar threshold. Football and basketball can have their own approval flows without losing the consolidated view your front office needs.",
  },
  {
    q: "Who owns our data — and can we get it out if we leave?",
    a: "You do. Every contract, term sheet, and audit log entry belongs to your department. You can export contracts, term sheets, and your full roster as PDF or CSV from inside the app at any time, and your data stays exportable for 90 days after cancellation. No exit fees, no professional-services charge to leave.",
  },
  {
    q: "Do we get charged per signature like DocuSign?",
    a: "No. There's no per-envelope or per-signature surcharge. Unlimited e-signature is included on every paid tier — both for in-app signing and emailed signatures to coaches and agents.",
  },
  {
    q: "What happens if our budget changes and we need to cancel?",
    a: "Paid plans are billed annually and run through the end of the term you've already paid for; you can cancel any time from your billing portal to stop the next renewal. You keep full access through the end of your year, and your data stays exportable for 90 days after that — so a budget change never costs you the audit history on a coach you've already hired.",
  },
  {
    q: "Does Zindeal keep us NCAA-compliant, and how do coaches actually sign?",
    a: "Yes on compliance: every approval is captured with role and timestamp, your pre-cleared clause language is reusable, NIL participation and outside-income disclosures live alongside base comp, and every contract change leaves an audit trail an internal reviewer or an NCAA reviewer can read in seconds. Zindeal doesn't replace your compliance office — it gives them a clean record instead of a Word doc and an inbox. On signing: coaches and agents don't need a seat or a login. They get a secure emailed link to review, comment, and e-sign, then keep a downloadable copy while your department keeps the source of truth.",
  },
];

const STATS = [
  { value: 46, prefix: "", suffix: "", decimals: 0, label: "NCAA sports covered" },
  { value: 6, prefix: "", suffix: "", decimals: 0, label: "Stages of a coach deal, in one workspace" },
  { value: 1, prefix: "", suffix: "", decimals: 0, label: "System of record for every coach deal" },
  { value: 24, prefix: "", suffix: "/7", decimals: 0, label: "We watch the system so you don't have to" },
];

const TIERS: NewPlan[] = ["free", "track", "draft", "enterprise"];

// Marketing-display tiers (Task #556). Backend plans in @workspace/plans
// (free/track/draft/enterprise) are unchanged and Stripe is unaffected — this
// map only relabels prices, names, and CTAs on the public landing page.
type TierDisplay = {
  name: string;
  audience: string;
  priceRange: string;
  implRange: string;
  description: string;
  highlights: string[];
  ctaLabel: string;
  badge?: string;
};

const TIER_DISPLAY: Record<NewPlan, TierDisplay> = {
  free: {
    name: "Essential",
    audience: "Single-program pilots and Olympic-sport teams",
    priceRange: "Contact for pricing",
    implRange: "Implementation scoped to your roster",
    description: "The system of record for your coach contracts — built to replace shared drives and Word docs.",
    highlights: [
      "Up to 25 coaches in one searchable workspace",
      "Term sheets, contract storage, and the full audit trail",
      "AI PDF import — drop in last year's contracts",
      "Unlimited e-signature — no per-envelope fees",
      "Standard exports (PDF, CSV) and email support",
    ],
    ctaLabel: "Talk to sales",
  },
  track: {
    name: "Command",
    audience: "Mid-major and D-II departments managing a full coaching staff",
    priceRange: "Contact for pricing",
    implRange: "Implementation scoped to your roster",
    description: "Negotiate, approve, and e-sign every coach deal in one place — with the obligations dashboard your front office needs.",
    highlights: [
      "Everything in Essential, with no roster cap",
      "Negotiation rounds with side-by-side version diff",
      "Approval routing by sport, role, or dollar threshold",
      "In-app and emailed e-signature — no per-envelope fees",
      "Live obligations & buyout exposure dashboard",
    ],
    ctaLabel: "Talk to sales",
    badge: "Most popular",
  },
  draft: {
    name: "Intelligence",
    audience: "Power Five and large multi-sport athletic departments",
    priceRange: "Contact for pricing",
    implRange: "Implementation scoped to your roster",
    description: "Full workforce intelligence — renewal pipelines, board-ready reporting, SSO, and a curated clause library.",
    highlights: [
      "Everything in Command",
      "Renewal pipeline + 90/60/30-day forecasting",
      "Custom approval policies per sport and dollar tier",
      "Curated clause library, branded templates",
      "SSO (Okta, Azure AD, Google Workspace)",
      "Custom roles, bulk import, and priority support",
    ],
    ctaLabel: "Talk to sales",
  },
  enterprise: {
    name: "Enterprise Plus",
    audience: "Conferences and multi-school athletic systems",
    priceRange: "Custom",
    implRange: "Conference-wide implementation",
    description: "Conference-wide deployment with custom integrations, dedicated success, and SLAs built for the most regulated environments.",
    highlights: [
      "Everything in Intelligence",
      "Multi-school and conference-level workspaces",
      "Custom integrations (HRIS, ERP, finance stack)",
      "Dedicated success manager and named SLAs",
      "Single-tenant deployment options",
      "Custom DPA, security review, and procurement support",
    ],
    ctaLabel: "Contact sales",
  },
};

type CompareValue = boolean | string;
const COMPARE_ROWS: { label: string; values: [CompareValue, CompareValue, CompareValue, CompareValue] }[] = [
  { label: "Coach roster, profiles & contract storage", values: [true, true, true, true] },
  { label: "AI PDF import — pull terms from existing contracts", values: [true, true, true, true] },
  { label: "Term sheets and standard exports (PDF, CSV)", values: [true, true, true, true] },
  { label: "Drafting from reusable templates", values: [false, true, true, true] },
  { label: "E-signature — no per-envelope fees", values: [true, true, true, true] },
  { label: "Approval workflows + full audit trail", values: [false, true, true, true] },
  { label: "Inline comments and @mentions", values: [false, true, true, true] },
  { label: "Branded PDF templates", values: [false, true, true, true] },
  { label: "Renewal pipeline + 90/60/30-day forecasting", values: [false, false, true, true] },
  { label: "Custom approval policies (by sport / dollar tier)", values: [false, false, true, true] },
  { label: "Curated clause library, versioned", values: [false, false, true, true] },
  { label: "SSO (Okta, Azure AD, Google Workspace)", values: [false, false, true, true] },
  { label: "Bulk import + custom roles", values: [false, false, true, true] },
  { label: "Multi-department / conference-wide rollout", values: [false, false, false, true] },
  { label: "Custom integrations (HRIS, ERP, data warehouse)", values: [false, false, false, true] },
  { label: "Dedicated CSM + quarterly business reviews", values: [false, false, false, true] },
  { label: "Custom DPA, security review, BAA on request", values: [false, false, false, true] },
  { label: "Support", values: ["Email", "Email + chat", "Priority", "White-glove + on-call"] },
];

export default function Landing() {
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  const { org } = useOrg();
  const signedInPlan = org?.plan as string | undefined;

  function ctaForTier(plan: NewPlan) {
    const display = TIER_DISPLAY[plan];
    const highlight = plan === "track";
    return (
      <Button
        asChild
        size="lg"
        className={`w-full font-semibold ${
          highlight
            ? "bg-[hsl(42,90%,50%)] hover:bg-[hsl(42,90%,45%)] text-[hsl(220,65%,12%)] border-0 shadow-lg"
            : "bg-white/10 hover:bg-white/20 text-white border border-white/20"
        }`}
        variant={highlight ? "default" : "outline"}
      >
        <a href={salesInquiryHref(`Zindeal ${display.name} inquiry`)}>
          {display.ctaLabel}
        </a>
      </Button>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans">

      {/* GLASSY STICKY NAV */}
      <GlassNav className="sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="Zindeal" className="w-8 h-8 rounded-full shadow-sm" />
            <div className="flex flex-col leading-none">
              <span className="font-serif font-semibold text-lg tracking-tight text-[hsl(220,65%,12%)]">Zindeal</span>
              <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-[hsl(0,0%,32%)]">Workforce Intelligence for College Athletics</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-7 text-sm text-gray-500 font-medium">
            <a href="#workflow" className="hover:text-gray-900 transition-colors">Coach lifecycle</a>
            <a href="#faq" className="hover:text-gray-900 transition-colors">Questions</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/sign-in">Sign in</Link>
            </Button>
            <Button asChild size="sm" className="bg-[hsl(220,55%,18%)] hover:bg-[hsl(220,65%,9%)] text-white shadow-sm">
              <a href={salesInquiryHref("Zindeal enterprise inquiry")}>Talk to sales</a>
            </Button>
          </div>
        </div>
      </GlassNav>

      {/* HERO */}
      <section className="relative pt-24 pb-28 px-6 overflow-hidden bg-[hsl(220,65%,12%)]">
        <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
          <div
            className="absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(ellipse 80% 60% at 50% -10%, hsl(220,55%,22%) 0%, transparent 70%)" }}
          />
          <div
            className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full hero-orb-1"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(80px)" }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full hero-orb-2"
            style={{ background: "radial-gradient(circle, hsl(220,60%,55%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "linear-gradient(hsl(0,0%,100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0,0%,100%) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        <div className="max-w-5xl mx-auto text-center relative">
          <FadeUp delay={0}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/8 text-xs font-semibold text-white/70 mb-8">
              <Zap className="h-3 w-3 text-[hsl(42,90%,50%)]" />
              Built by a former Power Five CHRO · For Athletic Departments
            </div>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1
              className="text-5xl md:text-7xl font-extrabold leading-[1.02] text-white"
              style={{ letterSpacing: "-0.035em" }}
            >
              Your <DisplayAccent>front office</DisplayAccent>
              <br />
              for coach contracts.
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="mt-7 text-lg md:text-xl text-white/65 max-w-2xl mx-auto leading-relaxed">
              Zindeal is the workforce intelligence platform built for college athletics — every term sheet, counter, approval, signature, and renewal in one workspace, with the obligations and audit trail your whole front office can pull from instantly.
            </p>
          </FadeUp>

          <FadeUp delay={0.3}>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
              <Button asChild size="lg" className="bg-[hsl(42,90%,50%)] hover:bg-[hsl(42,90%,45%)] text-[hsl(220,65%,12%)] font-bold px-8 h-12 text-base shadow-lg border-0">
                <a href={salesInquiryHref("Zindeal enterprise inquiry")}>
                  Talk to sales
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button size="lg" variant="ghost" className="h-12 text-base px-8 text-white/80 hover:text-white hover:bg-white/10 border border-white/20" asChild>
                <a href={salesInquiryHref("Zindeal — book a walkthrough")}>
                  Book a 20-min walkthrough
                </a>
              </Button>
            </div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <a href="#peeks" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors font-medium">
                See how it works <ArrowRight className="h-3.5 w-3.5 rotate-90" />
              </a>
              <button
                type="button"
                onClick={() => setWalkthroughOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors font-medium underline-offset-4 hover:underline"
                data-testid="cta-walkthrough-2min"
              >
                <Sparkles className="h-3.5 w-3.5" />
                See it in 2 minutes
              </button>
            </div>
            <div className="mt-5 flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-white/15" />
              <span className="text-xs text-white/35 font-medium">Already running your department here?</span>
              <div className="h-px w-16 bg-white/15" />
            </div>
            <div className="mt-3">
              <Button asChild size="sm" variant="ghost" className="text-white/70 hover:text-white hover:bg-white/10 border border-white/20 px-5 h-9 text-sm font-medium">
                <Link href="/sign-in">
                  Sign in to your workspace
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-xs text-white/30">Annual contracts · Implementation included · Cancel anytime</p>
          </FadeUp>

          {/* Live dashboard preview */}
          <FadeUp delay={0.4}>
            <HeroVisual />
          </FadeUp>
        </div>
      </section>

      {/* FOUNDER CREDIBILITY ROW */}
      <FounderRow />

      {/* FRONT OFFICE — capability grid (6 blocks) */}
      <PersonaTabs />

      {/* WORKFLOW — lifecycle (Six stages, one workspace) */}
      <div id="workflow">
        <LifecycleStrip />
      </div>

      {/* PRODUCT PEEKS */}
      <ProductPeeks />

      {/* INTEGRATIONS STRIP */}
      <section className="py-16 px-6 bg-[hsl(220,20%,97%)] border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="text-center mb-10">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">
                Sits next to the systems you already run
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Plays nice with the systems your department already runs on
              </h2>
              <p className="mt-3 text-gray-500 text-sm max-w-2xl mx-auto">
                Your e-signature vendor, your team calendars, your finance stack, your roster spreadsheet — keep them. Zindeal fits in next to them instead of asking you to rip and replace.
              </p>
            </div>
          </FadeUp>
          <StaggerChildren className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {INTEGRATIONS.map((it) => (
              <StaggerItem key={it.label}>
                <div className="bg-white border border-gray-100 rounded-xl px-3 py-4 flex flex-col items-center justify-center gap-2 text-center h-full" style={{ boxShadow: "var(--shadow-card)" }}>
                  <div className="w-10 h-10 rounded-lg bg-[hsl(220,55%,18%)] flex items-center justify-center">
                    <it.icon className="h-5 w-5 text-[hsl(42,90%,55%)]" />
                  </div>
                  <span className="text-xs font-semibold text-[hsl(220,65%,12%)] leading-tight">{it.label}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="py-20 px-6 bg-[hsl(220,65%,12%)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(80px)" }}
          />
        </div>
        <div className="max-w-5xl mx-auto relative">
          <FadeUp>
            <p className="text-center text-[10px] text-white/30 uppercase tracking-[0.18em] font-semibold mb-12">
              What your department gets on day one
            </p>
          </FadeUp>
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s) => (
              <StaggerItem key={s.label}>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl font-extrabold text-white kpi-value" style={{ letterSpacing: "-0.03em" }}>
                    <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} duration={1.8} />
                  </div>
                  <div className="mt-2 text-sm text-white/40 font-medium">{s.label}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* PRICING — hidden from live view per user direction; toggle SHOW_PRICING_SECTIONS above to restore */}
      {SHOW_PRICING_SECTIONS && (
      <section id="pricing" className="py-24 px-6 bg-[hsl(220,20%,97%)]">
        <div className="max-w-7xl mx-auto">
          <FadeUp>
            <div className="text-center mb-10">
              <h2 className="text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Built for college athletics. Priced for your department.
              </h2>
              <p className="mt-4 text-gray-500 text-lg max-w-2xl mx-auto">Annual subscription plus a one-time implementation, scoped to your roster size, sport mix, and integration needs. Pick the tier that fits your front office — pricing is shared on the call.</p>
              <p className="mt-2 text-xs uppercase tracking-wider text-gray-400 font-semibold">Annual contract · one invoice per year</p>
            </div>
          </FadeUp>
          <StaggerChildren className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((plan) => {
              const display = TIER_DISPLAY[plan];
              const highlight = plan === "track";
              const isCurrent = signedInPlan === plan;
              return (
                <StaggerItem key={plan}>
                  <HoverLift className="h-full" y={-4}>
                    <div
                      className={`rounded-2xl border p-7 flex flex-col h-full transition-all ${
                        isCurrent
                          ? "bg-white border-[hsl(158,64%,38%)] ring-2 ring-[hsl(158,64%,38%)]/30 shadow-xl"
                          : highlight
                          ? "bg-[hsl(220,65%,10%)] text-white border-transparent shadow-2xl lg:scale-[1.03] relative overflow-hidden"
                          : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-md"
                      }`}
                    >
                      {highlight && (
                        <div
                          className="absolute inset-0 opacity-30 pointer-events-none"
                          style={{ background: "linear-gradient(135deg, hsl(42,90%,50%)/0.15 0%, transparent 60%)" }}
                        />
                      )}
                      {highlight && (
                        <div
                          className="absolute inset-0 rounded-2xl pointer-events-none"
                          style={{ boxShadow: "inset 0 0 0 1px hsl(42,90%,50%)/0.2" }}
                        />
                      )}
                      {isCurrent ? (
                        <div className="self-start mb-4 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[hsl(158,64%,38%)]/15 text-[hsl(158,64%,38%)] border border-[hsl(158,64%,38%)]/25">
                          Your current plan
                        </div>
                      ) : display.badge && (
                        <div className="self-start mb-4 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[hsl(42,90%,50%)]/20 text-[hsl(42,90%,50%)] border border-[hsl(42,90%,50%)]/30">
                          {display.badge}
                        </div>
                      )}
                      <div className={`mb-1 font-semibold text-xs uppercase tracking-wider ${highlight ? "text-white/50" : "text-gray-400"}`}>
                        {display.name}
                      </div>
                      <div className={`mb-4 text-[11px] font-medium ${highlight ? "text-[hsl(42,90%,55%)]" : "text-[hsl(0,0%,32%)]"}`}>
                        {display.audience}
                      </div>
                      <div className="mb-1">
                        <span className={`text-xl font-extrabold kpi-value ${highlight ? "text-white" : "text-[hsl(220,65%,12%)]"}`}>{display.priceRange}</span>
                      </div>
                      <p className={`text-[11px] mb-4 ${highlight ? "text-white/45" : "text-gray-500"}`}>{display.implRange}</p>
                      <p className={`text-sm mb-6 leading-relaxed ${highlight ? "text-white/55" : "text-gray-500"}`}>{display.description}</p>
                      <ul className="space-y-3 flex-1 mb-6 relative">
                        {display.highlights.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className={`h-4 w-4 flex-shrink-0 mt-0.5 ${highlight ? "text-[hsl(42,90%,50%)]" : "text-[hsl(158,64%,38%)]"}`} />
                            <span className={highlight ? "text-white/80" : "text-gray-700"}>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="relative">{ctaForTier(plan)}</div>
                    </div>
                  </HoverLift>
                </StaggerItem>
              );
            })}
          </StaggerChildren>
          <FadeUp delay={0.2}>
            <p className="text-center text-xs text-gray-400 mt-8">
              Every tier includes unlimited e-signature with no per-envelope fees. Implementation covers data migration, clause-library setup, and (where applicable) SSO configuration. Final pricing is shared after a 30-minute scoping call so we can right-size your tier and implementation.
            </p>
          </FadeUp>
        </div>
      </section>
      )}

      {/* COMPARE PLANS — hidden from live view per user direction; toggle SHOW_PRICING_SECTIONS above to restore */}
      {SHOW_PRICING_SECTIONS && (
      <section id="compare" className="py-20 px-6 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <FadeUp>
            <div className="text-center mb-10">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">Compare plans</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Capability by tier
              </h2>
              <p className="mt-3 text-gray-500 text-sm max-w-2xl mx-auto">
                What scales with you as your front office grows — from a single-program pilot to a conference-wide rollout.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left font-semibold text-gray-500 text-xs uppercase tracking-wide px-5 py-4 w-[36%]">Capability</th>
                    {TIERS.map((p) => (
                      <th key={p} className={`text-center font-bold px-3 py-4 text-sm ${p === "track" ? "text-[hsl(220,65%,12%)] bg-amber-50/60" : "text-gray-800"}`}>
                        {p === "track" && <span className="block text-[9px] text-[hsl(42,90%,50%)] font-bold uppercase tracking-wider mb-0.5">★ Most popular</span>}
                        {TIER_DISPLAY[p].name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((row, i) => (
                    <tr key={row.label} className={`border-b border-gray-50 ${i % 2 === 1 ? "bg-gray-50/30" : ""}`}>
                      <td className="px-5 py-3 text-gray-800 font-medium text-sm">{row.label}</td>
                      {row.values.map((v, j) => (
                        <td key={j} className={`text-center px-3 py-3 ${TIERS[j] === "track" ? "bg-amber-50/40" : ""}`}>
                          {v === true ? (
                            <CheckCircle2 className="h-4 w-4 text-[hsl(158,64%,38%)] inline-block" />
                          ) : v === false ? (
                            <span className="text-gray-300 text-base">—</span>
                          ) : (
                            <span className="text-gray-700 text-xs font-medium">{v}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-gray-50/60 border-t border-gray-100">
                    <td className="px-5 py-3 font-semibold text-gray-800 text-sm">Annual subscription</td>
                    {TIERS.map((p) => (
                      <td key={p} className={`text-center px-3 py-3 text-gray-600 font-medium text-xs ${p === "track" ? "bg-amber-50/60" : ""}`}>
                        {TIER_DISPLAY[p].priceRange}
                      </td>
                    ))}
                  </tr>
                  <tr className="bg-gray-50/60">
                    <td className="px-5 py-3 font-semibold text-gray-800 text-sm">Implementation (one-time)</td>
                    {TIERS.map((p) => (
                      <td key={p} className={`text-center px-3 py-3 text-gray-600 font-medium text-xs ${p === "track" ? "bg-amber-50/60" : ""}`}>
                        {TIER_DISPLAY[p].implRange.replace(/^\+ /, "")}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-center text-xs text-gray-400">
              Each tier includes everything in the tier to its left. Talk to sales for a fit-by-program walkthrough.
            </p>
          </FadeUp>
        </div>
      </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 bg-[hsl(220,20%,97%)] border-t border-gray-100">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <div className="text-center mb-12">
              <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">
                Frequently asked
              </p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
                Questions athletic directors ask us first
              </h2>
              <p className="mt-3 text-gray-500 text-base">
                If your question isn't here, email{" "}
                <a href={salesInquiryHref("Zindeal question")} className="font-semibold text-[hsl(220,65%,12%)] underline underline-offset-2 hover:text-[hsl(220,55%,22%)]">
                  {SALES_EMAIL}
                </a>
                {" "}— a real person will reply.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Accordion type="single" collapsible className="bg-white border border-gray-100 rounded-2xl divide-y divide-gray-100" style={{ boxShadow: "var(--shadow-card)" }}>
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-b-0 px-6 first:rounded-t-2xl last:rounded-b-2xl">
                  <AccordionTrigger className="text-left text-base font-semibold text-[hsl(220,65%,12%)] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-sm leading-relaxed pb-5 pr-4">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>


      {/* BOTTOM CTA */}
      <section className="py-24 px-6 bg-[hsl(220,65%,12%)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/4 w-80 h-80 rounded-full opacity-15"
            style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
          <div
            className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, hsl(220,60%,55%) 0%, transparent 70%)", filter: "blur(60px)" }}
          />
        </div>
        <FadeUp>
          <div className="max-w-3xl mx-auto text-center relative">
            <div className="w-14 h-14 rounded-2xl bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center mx-auto mb-6">
              <Trophy className="h-7 w-7 text-[hsl(42,90%,50%)]" />
            </div>
            <h2 className="text-4xl font-extrabold text-white" style={{ letterSpacing: "-0.025em" }}>
              Bring your coach contracts up to where the rest of your department already is
            </h2>
            <p className="mt-4 text-white/50 text-lg leading-relaxed">
              Every coach deal — start to finish — in one workspace your whole front office can pull from. Term sheets, counters, approvals, signatures, renewals, and the audit trail behind them.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center flex-wrap">
              <Button asChild size="lg" className="bg-[hsl(42,90%,50%)] hover:bg-[hsl(42,90%,45%)] text-[hsl(220,65%,12%)] font-bold h-12 px-10 text-base shadow-lg border-0">
                <a href={salesInquiryHref("Zindeal enterprise inquiry")}>
                  Talk to sales
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="ghost" className="h-12 px-10 text-base text-white/80 hover:text-white hover:bg-white/10 border border-white/20">
                <a href={salesInquiryHref("Zindeal — book a walkthrough")}>
                  Book a 20-min walkthrough
                </a>
              </Button>
            </div>
            <p className="mt-3 text-xs text-white/30">Annual contracts · Implementation and onboarding included</p>
            <p className="mt-2 text-xs text-white/35">
              Or email{" "}
              <a href={salesInquiryHref()} className="underline underline-offset-2 hover:text-white">{SALES_EMAIL}</a>
            </p>
          </div>
        </FadeUp>
      </section>

      {/* FOOTER */}
      <footer className="bg-[hsl(220,65%,7%)] border-t border-white/8 pt-14 pb-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo-dark.svg" alt="Zindeal" className="w-7 h-7 rounded-full" />
                <span className="font-serif font-semibold text-white text-base">Zindeal</span>
              </div>
              <p className="text-white/35 text-xs leading-relaxed">
                The system of record for college coach contracts — from the first offer to the day they re-sign.
              </p>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Product</div>
              <ul className="space-y-2.5">
                {[["Coach lifecycle", "#workflow"], ["Inside the workspace", "#peeks"], ["Questions", "#faq"]].map(([label, href]) => (
                  <li key={label}>
                    <a href={href} className="text-white/45 hover:text-white/80 transition-colors text-sm">{label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Company</div>
              <ul className="space-y-2.5">
                {[["Sign in", "/sign-in"]].map(([label, href]) => (
                  <li key={label}>
                    <Link href={href} className="text-white/45 hover:text-white/80 transition-colors text-sm">{label}</Link>
                  </li>
                ))}
                <li><a href={salesInquiryHref("Zindeal enterprise inquiry")} className="text-white/45 hover:text-white/80 transition-colors text-sm">Contact Sales</a></li>
                <li><a href={salesInquiryHref("Zindeal support")} className="text-white/45 hover:text-white/80 transition-colors text-sm">Support</a></li>
                <li><a href={STATUS_PAGE_URL} target="_blank" rel="noopener noreferrer" className="text-white/45 hover:text-white/80 transition-colors text-sm">System Status</a></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-4">Legal</div>
              <ul className="space-y-2.5">
                {[["Terms of Service", "/terms"], ["Privacy Policy", "/privacy"], ["Subprocessors", "/subprocessors"], ["Security", "/security"]].map(([label, href]) => (
                  <li key={label}>
                    <Link href={href} className="text-white/45 hover:text-white/80 transition-colors text-sm">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-white/8 flex flex-col md:flex-row items-center justify-between gap-3">
            <span className="text-white/25 text-xs">© {new Date().getFullYear()} Zindeal. All rights reserved.</span>
            <span className="text-white/20 text-xs">Built for Division I, II, and III programs</span>
          </div>
        </div>
      </footer>
      {walkthroughOpen && (
        <TwoMinuteWalkthrough onClose={() => setWalkthroughOpen(false)} />
      )}
    </div>
  );
}

const WALKTHROUGH_SLIDES: { title: string; body: string; bullets: string[] }[] = [
  {
    title: "1 · Every coach in one place",
    body: "Pull up any coach in two seconds — current contract, last review, insurance, even the buyout math — from a single searchable workspace.",
    bullets: [
      "Every coach's full file in one click",
      "Drag-and-drop a stack of PDFs, we extract the deal points for you",
      "Sort by sport, level, or any term in the contract",
    ],
  },
  {
    title: "2 · Negotiate in one thread",
    body: "Send the offer, capture every counter, and route signoffs through the right approvers — all on one page. Every version is preserved with a full diff.",
    bullets: [
      "See exactly what changed between v3 and v4, side by side",
      "Approvals route automatically by sport, role, or dollar threshold",
      "Every action is logged automatically — who, what, when",
    ],
  },
  {
    title: "3 · Real-time obligations forecasting",
    body: "Open one screen and see exactly what your department owes coaches this year, next year, and the year after — broken down by sport. Board-ready PDFs two clicks away.",
    bullets: [
      "Total obligations by program and by year",
      "Buyout exposure surfaced as it shifts",
      "Export to PDF or CSV anytime",
    ],
  },
  {
    title: "4 · Renewal pipeline you can plan around",
    body: "Every contract is tracked to its renewal date. Ninety, sixty, and thirty days out — insurance, options, key dates — the right person on your team gets a heads-up.",
    bullets: [
      "90-day expiry alerts go out on their own",
      "Insurance lapses caught before the policy actually ends",
      "Compliance items don't slip through because someone went on vacation",
    ],
  },
  {
    title: "5 · See it with your team",
    body: "Book a guided walkthrough with real coaches, contracts, and numbers tailored to how your athletic department works — no self-serve sandbox on the public site.",
    bullets: [
      "Live product session with our team",
      "Workflows walked through against your use cases",
      "20-minute walkthrough — talk to sales to schedule",
    ],
  },
];

function TwoMinuteWalkthrough({ onClose }: { onClose: () => void }) {
  const [idx, setIdx] = useState(0);
  const slide = WALKTHROUGH_SLIDES[idx];
  const last = idx === WALKTHROUGH_SLIDES.length - 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="2-minute product walkthrough"
      className="fixed inset-0 z-[200] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="walkthrough-modal"
    >
      <div
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 rounded-md p-2 hover:bg-slate-100 z-10"
          aria-label="Close walkthrough"
          data-testid="walkthrough-close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white px-8 py-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold opacity-80">
            <Sparkles className="h-4 w-4" />
            See it in 2 minutes
          </div>
          <h2 className="mt-2 text-2xl font-bold">{slide.title}</h2>
        </div>
        <div className="px-8 py-6">
          <p className="text-slate-700 leading-relaxed">{slide.body}</p>
          <ul className="mt-4 space-y-2">
            {slide.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {WALKTHROUGH_SLIDES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-indigo-600" : "w-1.5 bg-slate-300"}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
                Back
              </Button>
              {last ? (
                <Button asChild size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="walkthrough-cta-demo">
                  <a href={salesInquiryHref("Zindeal — book a walkthrough")}>Talk to sales</a>
                </Button>
              ) : (
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setIdx((i) => Math.min(WALKTHROUGH_SLIDES.length - 1, i + 1))} data-testid="walkthrough-next">
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
